import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { AccessToken, IAlertsResponse, IUserGoalPreferences, IUserPersonalInfo, IUserSessionData } from '../models/user.model';
import { catchError, map } from 'rxjs/operators';
import { IStepConfirmValidation, RegisterReceiptData } from '../models/register.model';
import { BillingRedirectError } from 'app/errors/billing-redirect.error';
import { ConfirmationRedirectError } from 'app/errors/confirmation-redirect.error';
import { IApiModel, IListApiModel } from '../models/api.model';

import { environment } from '../../../environments/environment';
import { IAccount } from '../models/account.model';
import { IUserApiKey, IUserApiKeyCreated } from '../models/api-key.model';
import { EDeletePeriod } from 'app/dialogs/manage-deleted-account/manage-deleted-account.component';

export interface ITestNotificationResult {
  success: boolean;
  sent_to: string[];
  test_id: string;
  message_id?: string;
  status: 'queued' | 'delivered' | 'failed' | 'partial';
  delivered: boolean;
  delivered_to?: string[];
  failed_to?: string[];
  pending_to?: string[];
}

export interface ITestNotificationStatus {
  test_id: string;
  message_id?: string | null;
  status: 'queued' | 'delivered' | 'failed' | 'partial';
  delivered: boolean;
  delivered_to?: string[];
  failed_to?: string[];
  pending_to?: string[];
}

@Injectable()
export class UserService {
  constructor(private http: HttpClient) {
  }

  confirm(token: string): Observable<AccessToken | null> {
    return this.http
      .put<AccessToken | null>(environment.apiUrl + `/v1/users/confirm`, {
        confirmation_token: token,
      })
      .pipe(
        map((result: AccessToken | null) => {
          if (result) {
            localStorage.setItem('token', JSON.stringify(result));
            localStorage.setItem('comming_from', 'verify');
          }

          return result;
        }),
      );
  }

  acceptPolicy(idAccount): Observable<IApiModel> {
    return this.http.post<IApiModel>(environment.apiUrl + `/v1/users/policy`, {
      account_id: idAccount,
    });
  }

  logout(performReturn: boolean = false): Observable<AccessToken | null> {
    return this.http.get<AccessToken | null>(
      environment.apiUrl + '/v1/users/logout',
      {
        params: {
          return_original: performReturn.toString(),
        },
      },
    );
  }

  login({ username, password }): Observable<AccessToken | null> {
    return this.http
      .post<any>(environment.apiUrl + `/v1/users/login`, {
        email: username,
        password,
      })
      .pipe(
        map(response => {
          if (response) {
            localStorage.setItem('token', JSON.stringify(response));
            return response;
          }

          return null;
        }),
        catchError(error => {
          if (
            error instanceof HttpErrorResponse &&
            error.status === 400 &&
            [1002, 1003].indexOf(error.error.code) !== -1
          ) {
            if (error.error.code === 1002) {
              return throwError(new BillingRedirectError());
            } else if (error.error.code === 1003) {
              return throwError(new ConfirmationRedirectError());
            }
          }

          return throwError(error);
        }),
      );
  }

  loginWithSso(provider: string, credential: Record<string, string>): Observable<any> {
    return this.http
      .post<any>(environment.apiUrl + `/v1/users/login/${provider}`, credential)
      .pipe(
        map(response => {
          // ApiResponseInterceptor already unwraps `{ data: ... }` into the body.
          const payload = response?.needs_registration
            ? response
            : response?.data?.needs_registration
              ? response.data
              : response;
          if (payload?.needs_registration) {
            localStorage.removeItem('token');
            return payload;
          }
          if (payload?.access_token) {
            localStorage.setItem('token', JSON.stringify(payload));
          }
          return payload;
        }),
      );
  }

  getSsoConfig(): Observable<{
    enabled_providers: string[];
    google: { client_id: string; mode?: 'redirect' | 'button' };
    spacescout: { client_id: string; authorize_url: string };
  }> {
    // ApiResponseInterceptor already unwraps `{ data: ... }` into the body.
    return this.http.get<{
      enabled_providers: string[];
      google: { client_id: string; mode?: 'redirect' | 'button' };
      spacescout: { client_id: string; authorize_url: string };
    }>(environment.apiUrl + `/v1/users/sso-config`);
  }

  validateEmailAddress(data): Observable<IStepConfirmValidation> {
    return this.http.post<any>(environment.apiUrl + `/v1/users/register`, {
      ...data,
      validate_only: true,
    });
  }

  payFee(userData, data): Observable<RegisterReceiptData> {
    if (userData === null) {
      return this.http.post<any>(environment.apiUrl + '/v1/users/payment', {
        ...data,
      });
    }

    return this.http.post<any>(environment.apiUrl + `/v1/users/register`, {
      ...userData,
      ...data,
    });
  }

  getSessionData(): Observable<IUserSessionData> {
    return this.http.get<IUserSessionData>(
      environment.apiUrl + '/v1/users/session',
    );
  }

  getAlerts(type: string): Observable<IAlertsResponse> {
    return this.http.get<IAlertsResponse>(
      environment.apiUrl + '/v1/users/alerts',
      { params: { type } },
    );
  }

  changePassword(
    accountId: number,
    oldPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Observable<any> {
    return this.http.put<any>(environment.apiUrl + '/v1/users/password', {
      account_id: accountId,
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  updateGoalPreferences(
    accountId: number,
    prefs: IUserGoalPreferences,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      ...prefs,
    });
  }

  updateUserTimezone(
    accountId: number,
    timezone: string,
    observeDst: boolean,
  ): Observable<{
    settings: number;
  }> {
    return this.http.put<{
      settings: number;
    }>(environment.apiUrl + `/v1/settings/${accountId}`, {
      time_zone: timezone,
      dst: observeDst,
    });
  }

  updateUserSmsDetails(
    accountId: number,
    sendSms: boolean, mobileNumber: string
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      send_sms: sendSms,
      mobile_phone: mobileNumber
    });
  }

  updateUserPersonalInfo(
    accountId: number,
    info: IUserPersonalInfo,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      ...info,
    });
  }

  toggleEmailPreview(
    accountId,
    currentValue,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + `/v1/users/${accountId}`, {
      emails_preview: !currentValue,
    });
  }

  toggleHints(
    accountId: number,
    value: boolean,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + '/v1/users/' + accountId, {
      popup_tips: value,
    });
  }

  toggleVipFeature(
    accountId: number,
    value: boolean,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + '/v1/settings/' + accountId, {
      vip_mode: value,
    });
  }

  toggleUnsubscribe(
    accountId: number,
    state: boolean,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + '/v1/settings/' + accountId, {
      unsubscribe: state,
    });
  }

  getLabels(): Observable<any> {
    return this.http.get<any>(environment.apiUrl + '/v1/users/view-labels');
  }

  updateLabels(data): Observable<any> {
    return this.http.post<any>(environment.apiUrl + `/v1/users/manage-labels`, {
      ...data,
    });
  }

  getBillingInfo(accountId: number): Observable<any> {
    return this.http.get<any>(environment.apiUrl + `/v1/users/${accountId}`);
  }

  getAccountInf(accountId): Observable<any> {
    return this.http.get<any>(environment.apiUrl + `/v1/users/${accountId}`);
  }

  updateBillingInfo(accountId, data): Observable<any> {
    return this.http.put<any>(environment.apiUrl + `/v1/users/${accountId}`, {
      ...data,
    });
  }

  updateAccountPayment(accountId: number, paymentData: any): Observable<{ id_user: number }> {
    return this.http.put<{ id_user: number }>(environment.apiUrl + `/v1/users/${accountId}/payment`, paymentData);
  }

  getPaymentMethods(accountId: number): Observable<{
    default_payment_method_id: string | null;
    items: Array<{
      id: string;
      is_default: boolean;
      summary: Record<string, unknown>;
    }>;
  }> {
    return this.http.get<{
      default_payment_method_id: string | null;
      items: Array<{
        id: string;
        is_default: boolean;
        summary: Record<string, unknown>;
      }>;
    }>(environment.apiUrl + `/v1/users/${accountId}/payment-methods`);
  }

  setDefaultPaymentMethod(
    accountId: number,
    paymentMethodId: string,
  ): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      environment.apiUrl + `/v1/users/${accountId}/payment-methods/default`,
      { payment_method_id: paymentMethodId },
    );
  }

  /** Payment Element: create SetupIntent for an authenticated user (add/change default payment method). */
  createSetupIntent(accountId: number): Observable<{
    client_secret: string;
    setup_intent_id: string;
    stripe_publishable_key: string;
  }> {
    return this.http.post<{
      client_secret: string;
      setup_intent_id: string;
      stripe_publishable_key: string;
    }>(environment.apiUrl + `/v1/users/${accountId}/setup-intent`, {});
  }

  /**
   * Registration step 1: create account + Stripe customer + SetupIntent (no card in API body).
   */
  registerPaymentElementInit(body: Record<string, any>): Observable<{
    success: boolean;
    account_id: number;
    client_secret: string;
    setup_intent_id: string;
    stripe_publishable_key: string;
  }> {
    const { use_payment_element: _u, terms_accepted: termsIn, ...rest } = body;
    return this.http.post<{
      success: boolean;
      account_id: number;
      client_secret: string;
      setup_intent_id: string;
      stripe_publishable_key: string;
    }>(environment.apiUrl + '/v1/users/register', {
      ...rest,
      use_payment_element: true,
      terms_accepted: termsIn !== undefined ? termsIn : true,
    });
  }

  registerPaymentElementComplete(body: {
    account_id: number;
    setup_intent_id: string;
    plan_id: number;
    discount?: string;
  }): Observable<any> {
    return this.http.post(environment.apiUrl + '/v1/users/register/complete-setup', body);
  }

  deleteUser(accountId: number, forward?: boolean, period?: EDeletePeriod | number): Observable<any> {
    const params: any = {};
    if (forward !== undefined) {
      params.deleted_forward = forward;
    }
    if (period !== undefined) {
      params.period = period;
    }
    return this.http.delete<any>(environment.apiUrl + `/v1/users/${accountId}`, { 
      body: params
     });
  }

  resetPassword(
    accountId: number,
    newPassword: string,
    confirmPassword: string,
  ): Observable<{
    account_id: number;
  }> {
    return this.http.put<{
      account_id: number;
    }>(environment.apiUrl + '/v1/users/password', {
      account_id: accountId,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  setNotificationsEmail(
    accountId: number,
    email: string,
    notification: boolean = false,
    forward: boolean = false,
  ): Observable<{
    success: boolean;
  }> {
    return this.http.post<{
      success: boolean;
    }>(environment.apiUrl + '/v1/users/notification_email', {
      account_id: accountId,
      email,
      notification,
      forward,
    });
  }

  sendTestNotification(
    accountId: number,
    email?: string,
  ): Observable<ITestNotificationResult> {
    return this.http.post<ITestNotificationResult>(
      environment.apiUrl + '/v1/messages/test-notification',
      {
        account_id: accountId,
        ...(email ? { email } : {}),
      },
    );
  }

  getTestNotificationStatus(
    accountId: number,
    testId: string,
    messageId?: string,
  ): Observable<ITestNotificationStatus> {
    return this.http.get<ITestNotificationStatus>(
      environment.apiUrl + '/v1/messages/test-notification',
      {
        params: {
          account_id: accountId,
          test_id: testId,
          ...(messageId ? { message_id: messageId } : {}),
        },
      },
    );
  }

  getVsmUsers(): Observable<IListApiModel<IAccount>> {
    return this.http.get<IListApiModel<IAccount>>(
      environment.apiUrl + '/v1/users',
      {
        params: {
          user_type: 'vsm',
        },
      },
    );
  }

  setForwarding(accountId: number, forward: boolean, period: number): Observable<{success: boolean}>{
    return this.http.put<{success: boolean}>(environment.apiUrl + `/v1/users/forward`, {
      forward,
      period,
      account_id: accountId
    });
  }

  listApiKeys(): Observable<IUserApiKey[]> {
    return this.http.get<IUserApiKey[]>(environment.apiUrl + '/v1/users/api-keys');
  }

  createApiKey(name: string): Observable<IUserApiKeyCreated> {
    return this.http.post<IUserApiKeyCreated>(environment.apiUrl + '/v1/users/api-keys', {
      name,
    });
  }

  revokeApiKey(id: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      environment.apiUrl + `/v1/users/api-keys/${id}`,
    );
  }
}
